import React from "react";
import LegalDocumentPage from "./LegalDocumentPage";

const sections = [
  {
    num: "01", title: "Introduction",
    body: "This Privacy Policy describes how Snows ProAm collects, uses, shares, and protects personal data when you use our App, website, school challenges, and related services.",
  },
  {
    num: "02", title: "Information We Collect",
    body: null,
    bullets: [
      { label: "Personal Information", detail: "Name, email address, and other information you provide during registration." },
      { label: "School and Challenge Information", detail: "Student account type, school or institute membership, year group or team, step totals, challenge progress, and leaderboard position." },
      { label: "Health and Fitness Data", detail: "Step count, distance, calories, workouts, motion, and related activity data when you connect a supported health service or use activity tracking." },
      { label: "Geolocation Data", detail: "Location or route information when you give device permission and actively use a location-based feature." },
      { label: "Usage Data", detail: "Information about your use of the App, including the features you use and the time spent on the App." },
      { label: "Device and Advertising Data", detail: "Device or app identifiers, advertising identifiers where available, operating system, app version, and related technical information used for security, analytics, advertising measurement, and install attribution." },
      { label: "App Event Data", detail: "App activation, completed registration, and successful feature actions such as saving an activity, joining a club or session, or creating a community. We use event data to understand feature use and measure advertising performance." },
    ],
  },
  {
    num: "03", title: "Children and Student Accounts",
    body: "You must be at least 12 years old to use Snows ProAm. In Ireland, a child is a person under 18. We provide a higher level of protection for users who identify as secondary-school students and explain this processing in clear language for students, parents, guardians, and schools.",
    subSections: [
      {
        title: "Student safeguards",
        list: [
          "Student profiles stay in Ghost Mode and cannot share a live map location with other users.",
          "Students cannot access or upload Highlights, access public Communities or global chats, or add friends.",
          "Student leaderboard views are limited to student accounts. School leaderboards may show the student's name or username, school or year-group membership, step totals, progress, and rank to authorised participants or administrators.",
          "A route may still be processed privately when the student deliberately starts activity tracking and grants device permission. This is separate from live location sharing.",
        ],
      },
      {
        title: "Parents, guardians, and schools",
        list: [
          "Where we rely on consent to provide an online service directly to a child under 16 in Ireland, the consent must be authorised by a parent or guardian.",
          "Schools should give students and parents their own privacy information and identify the school's lawful basis, approval process, retention arrangements, and contact point.",
          "Children have their own data-protection rights. A parent or guardian does not automatically own a child's personal data, although they may help the child exercise those rights where appropriate.",
        ],
      },
    ],
    note: <>Read the plain-language <a href="/student-privacy" style={{ textDecoration: "underline" }}>Student Privacy &amp; Safety Notice</a>.</>,
  },
  {
    num: "04", title: "How We Use Your Information",
    body: "We use your information to:",
    list: [
      "Provide and improve our Service.",
      "Facilitate bookings and payments.",
      "Match you with other users for sports activities.",
      "Communicate with you about your account and bookings.",
      "Ensure the security of our App and users.",
      "Understand how the App is used and measure whether advertising on services such as Facebook and Instagram leads to installs, registrations, and successful in-app actions.",
    ],
  },
  {
    num: "05", title: "Sharing Your Information",
    body: "We may share your information with:",
    bullets: [
      { label: "Service Providers", detail: "Third-party companies that provide services on our behalf, such as payment processing and geolocation services." },
      { label: "Schools and Challenge Administrators", detail: "Information needed to organise a school programme, verify participation, provide support, and show eligible leaderboards, subject to access controls and each party's responsibilities." },
      { label: "Analytics and Advertising Partners", detail: "Google and its affiliates may process limited device, app, advertising, consent, network, diagnostic, and interaction data to serve and measure AdMob ads and prevent fraud. For adult accounts only, Meta Platforms Ireland Limited and its affiliates may receive permitted app-interaction, device, and advertising-identifier data through Meta App Events to measure advertising on Facebook and Instagram." },
      { label: "Law Enforcement", detail: "If required by law or if we believe that such action is necessary to comply with legal obligations or protect our rights." },
    ],
  },
  {
    num: "06", title: "Geolocation Data",
    body: "We use geolocation data to provide location-based services, such as finding nearby sports facilities and matching you with other users. Geolocation data is collected through your device's GPS when you give us permission.",
    subSections: [
      {
        title: "How We Request and Use Geolocation Data",
        list: [
          "Permission: We will request your permission to access your location the first time you use any location-based feature.",
          "Ongoing Consent: If you grant access, we collect your location data in real time while the app is open and in use.",
          "Background Location: If you start an activity-tracking session, location may continue to be processed while that session is active so route, pace, and distance can be recorded when the app is not on screen.",
          "Usage: We use your location to show nearby facilities, help match users by proximity, and improve venue suggestions.",
        ],
      },
      {
        title: "Managing Geolocation Permissions",
        list: [
          "You can disable geolocation services at any time through your device's settings. Doing so will limit location-dependent features.",
        ],
      },
      {
        title: "Public route discovery",
        list: [
          "New GPS activities are saved privately, and their full routes appear in your activity history. Adult account holders can choose to publish a route to the worldwide map; students cannot publish routes.",
          "Before publication, the app makes the original activity private and removes the first and last 200 metres from the public route copy. Places along the remaining route may still be recognisable.",
          "Other people can view and replay a published route with your username, activity title, date, sport, and distance. Published routes also contribute to community heatmaps, including areas where only one athlete has shared a route. Overlapping activity from more athletes increases the heat.",
          "You can remove a published route from discovery in Activity History. Previously viewed or saved copies outside Snows ProAm may remain with others.",
        ],
      },
    ],
  },
  {
    num: "07", title: "Use of Step Data for Fitness and Track Rankings",
    body: "If you use Fitness, Track, school challenge, or step-tracking features, step data is used to calculate progress and leaderboard rankings. The audience depends on the account and leaderboard type. Student accounts receive the additional restrictions described above.",
    subSections: [
      {
        title: "How Step Data is Used",
        list: [
          "Your steps are tracked and used to calculate your rank.",
          "Your ranking may be displayed regionally, nationally, and globally.",
          "Other eligible users and relevant institute administrators may view the profile and step information shown on a leaderboard.",
        ],
      },
    ],
    note: "You may contact support@snowsproam.com about leaderboard visibility, correction, restriction, or deletion, or delete your account through App Settings.",
  },
  {
    num: "08", title: "In-App Advertising, Consent, and Measurement",
    body: "The Snows ProAm mobile app shows Google AdMob banner and interstitial ads. We also use Meta App Events for adult accounts to understand whether advertising on Facebook or Instagram results in an installation, activation, registration, or another successful in-app action. Google and Meta process information under their own terms and privacy policies.",
    subSections: [
      {
        title: "Google AdMob ads",
        list: [
          "Google may process an IP address and approximate location, app interactions, ad views and clicks, diagnostics, performance or crash information, consent signals, and device, app, or advertising identifiers where permitted. Google uses this information to serve and measure ads, provide analytics, secure the service, and prevent fraud and abuse.",
          "Adults may receive personalised ads only where their region, Google consent choice, platform rules, and device settings permit it. Adults can reopen Google ad privacy choices from App Settings.",
          "Accounts under 18 are tagged for protected child or teen treatment, receive G-rated inventory, and are not offered personalised advertising. A user who declines personalisation can still use the App and may continue to see non-personalised or limited ads.",
        ],
      },
      {
        title: "Meta measurement for adults",
        list: [
          "Meta App Events and Meta advertising-identifier collection remain disabled until a completed profile shows the user is at least 18. Adult events may include app activation, registration, successful feature actions, and limited context such as sport, source screen, or session type.",
          "We do not intentionally include a name, email address (including an Apple Hide My Email address), phone number, chat message, payment credential, precise GPS location, activity route, food entry, step count, or health measurement in Meta custom-event parameters.",
          "Meta may receive standard device, app, operating-system, network, and advertising-identifier information when its SDK is enabled and the device, platform settings, and applicable law permit it.",
        ],
      },
      {
        title: "Your choices and sensitive-data limits",
        list: [
          "Where required, Google's consent form lets adults accept, refuse, or manage personalised-ad choices. Refusing consent never blocks use of the App.",
          "On iPhone, Snows ProAm asks adults for App Tracking Transparency permission before enabling advertiser tracking. Declining does not prevent App use, but limits identifier-based personalisation, attribution, and measurement.",
          "On Android, you can manage, reset, or delete your advertising identifier through your device's privacy or ads settings. Available controls vary by Android version and device manufacturer.",
          "We do not provide health or fitness data, HealthKit or Health Connect data, exact step totals, GPS routes, precise location, private messages, user content, payment details, names, or email addresses to Google or Meta for personalised advertising.",
        ],
      },
    ],
    note: <>
      Learn how Google uses partner app data in <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>Google's partner technologies notice</a>, read the <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>Google Privacy Policy</a>, and read the <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>Meta Privacy Policy</a>. Where consent is required, you may withdraw it without affecting earlier lawful processing.
    </>,
  },
  {
    num: "09", title: "Data Security",
    body: "We take the security of your information very seriously and employ industry-standard measures. While no method of transmission or storage can be guaranteed to be completely secure, we are continuously improving our protocols.",
  },
  {
    num: "10", title: "Data Retention",
    body: "We retain your information for as long as your account is active or as needed to provide the Service. We may also retain information as required by law or to enforce our agreements. Third parties such as Google and Meta retain information under their own policies and legal obligations.",
  },
  {
    num: "11", title: "Your Rights",
    body: "You have the right to:",
    list: [
      "Access, correct, or delete your personal information.",
      "Object to or restrict processing.",
      "Withdraw your consent at any time.",
      "Complain to the Irish Data Protection Commission if you believe your data-protection rights have been infringed.",
    ],
    note: "Contact us at support@snowsproam.com to exercise your rights.",
  },
  {
    num: "12", title: "Account and Data Deletion",
    body: "You can request deletion of your Snows ProAm account and the associated personal data without needing to contact us first.",
    subSections: [
      {
        title: "Delete your account in the App",
        list: [
          "Sign in to Snows ProAm and open Settings.",
          "Select Delete Account, type DELETE when prompted, and confirm the request.",
          "The App sends an authenticated deletion request, removes your sign-in account and the account data covered by our deletion process, signs you out, and clears locally stored app data from that device.",
        ],
      },
      {
        title: "If you cannot access the App",
        list: [
          "Email support@snowsproam.com from the email address associated with your account and use the subject “Account deletion request”.",
          "We may ask for limited information to verify that you control the account before completing the request.",
        ],
      },
      {
        title: "What is deleted or retained",
        list: [
          "We delete the authentication account, profile information, and account-linked sports, social, fitness, notification, team, and community records covered by our deletion process. Shared content may be deleted or de-identified where reasonably possible.",
          "We may retain limited payment and transaction records, tax or accounting records, fraud-prevention records, safety and moderation records, and records required to establish or defend legal claims. These records are restricted to those purposes and are normally retained for up to six years after account closure, or longer only where applicable law or an active legal matter requires it.",
          "Information already received by a third party, including Google or Meta, is processed under that third party's privacy policy and controls. Deleting your Snows ProAm account stops future account activity, but it does not automatically delete information held independently by those third parties.",
        ],
      },
    ],
    note: "Account deletion is permanent. If you only want specific information corrected or deleted, contact support@snowsproam.com instead of deleting the entire account.",
  },
  {
    num: "13", title: "Changes to This Privacy Policy",
    body: "We may update this Privacy Policy from time to time. Continued use of the app after changes means you accept the new policy.",
  },
  {
    num: "14", title: "Contact Us",
    body: "If you have any questions about this Privacy Policy, contact us at support@snowsproam.com.",
  },
];

export default function PrivacyPolicy() {
  return <LegalDocumentPage title="Privacy Policy" description="How Snows ProAm collects, uses, shares and protects your personal information." effective="27 September 2026" sections={sections} />;
}
